import { saveService } from '../services/SaveService';
import { mapService } from '../services/MapService';
import { dungeonService } from '../services/DungeonService';
import { DungeonEntityType } from '../types/enums';
import { LOCATION_REGISTRY } from '../data/locations.config';
import { MonsterEntity } from '../entities/MonsterEntity';
import { MapCategory } from '../types/map.types';
import { MonsterMapIcon } from '../data/monsters.config';

export const MAP_PALETTE = {
  undiscovered: '#241c10',
  room: '#d8c089',
  corridor: '#c0a877',
  wall: '#7a5a34',
  water: '#4f7da6',
  frameOuter: '#8a5a2b',
  frameInner: '#5a3a1a',
  player: '#2fb24f',
} as const;

interface MarkerStyle {
  color: string;
  glyph: string;
  /** Optional custom icon (small PNG URL). Falls back to glyph/shape. */
  icon?: string;
}

export const MAP_CATEGORY_STYLE: Record<MapCategory, MarkerStyle> = {
  exit: { color: '#6fb84a', glyph: '▼' },
  chest: { color: '#e8b53a', glyph: '▣' },
  shop: { color: '#5aa9c8', glyph: '⚖' },
  altar: { color: '#a877d8', glyph: '✦' },
  spring: { color: '#6fc6b0', glyph: '♨' },
  trap: { color: '#d06a4a', glyph: '▲' },
};

export const MONSTER_ICON_STYLE: Record<MonsterMapIcon, MarkerStyle> = {
  normal: { color: '#b83020', glyph: '' },
  elite: { color: '#c8641a', glyph: '' },
  miniboss: { color: '#8a3ad0', glyph: '' },
  boss: { color: '#b01038', glyph: '' },
};

export interface DiscoveredKinds {
  categories: Set<MapCategory>;
  icons: Set<MonsterMapIcon>;
}

export interface DungeonMapDrawOptions {
  ctx: CanvasRenderingContext2D;
  pixelSize: number;
  revealAll: boolean; // devMode: ignore fog
  showGlyphs: boolean; // true = full screen (location glyph), false = minimap
  showRoomDebug: boolean; // devMode room overlay (full screen only)
}

const iconCache = new Map<string, HTMLImageElement>();
function readyIcon(url?: string): HTMLImageElement | null {
  if (!url) {
    return null;
  }
  let img = iconCache.get(url);
  if (!img) {
    img = new Image();
    img.src = url;
    iconCache.set(url, img);
  }
  return img.complete && img.naturalWidth > 0 ? img : null;
}

const roomDebugColor = (index: number) => `hsl(${(index * 67) % 360} 80% 55%)`;

export function drawDungeonMap(opts: DungeonMapDrawOptions): DiscoveredKinds {
  const { ctx, pixelSize: px, revealAll, showGlyphs, showRoomDebug } = opts;
  const found: DiscoveredKinds = { categories: new Set(), icons: new Set() };
  const mapData = saveService.getMapData();
  if (!mapData) {
    return found;
  }
  const { width, height, tiles } = mapData;
  const discovered = mapService.discoveredTiles;
  const seen = (x: number, y: number) =>
    revealAll || discovered.has(`${x},${y}`);
  const py = (y: number) => (height - 1 - y) * px;

  ctx.fillStyle = MAP_PALETTE.undiscovered;
  ctx.fillRect(0, 0, width * px, height * px);

  for (const tile of tiles) {
    if (!seen(tile.x, tile.y)) {
      continue;
    }
    let color: string;
    if (tile.type === 'water') {
      color = MAP_PALETTE.water;
    } else if (tile.type === 'ground') {
      color =
        mapService.getRoomIndexAt(tile.x, tile.y) === -1
          ? MAP_PALETTE.corridor
          : MAP_PALETTE.room;
    } else {
      color = MAP_PALETTE.wall;
    }
    ctx.fillStyle = color;
    ctx.fillRect(tile.x * px, py(tile.y), px, px);
  }

  if (showRoomDebug) {
    ctx.globalAlpha = 0.35;
    mapService.getRooms().forEach((room, index) => {
      ctx.fillStyle = roomDebugColor(index);
      for (const c of room.cells) {
        ctx.fillRect(c.x * px, py(c.y), px, px);
      }
    });
    ctx.globalAlpha = 1;
  }

  for (const loc of dungeonService.locationService.getPlacedLocations()) {
    const cat = LOCATION_REGISTRY[loc.id]?.mapCategory;
    if (!cat) {
      continue;
    }
    let visible = false;
    for (let dy = 0; dy < loc.height && !visible; dy++) {
      for (let dx = 0; dx < loc.width && !visible; dx++) {
        if (seen(loc.x + dx, loc.y - dy)) {
          visible = true;
        }
      }
    }
    if (!visible) {
      continue;
    }
    found.categories.add(cat);
    const style = MAP_CATEGORY_STYLE[cat];
    const cx = (loc.x + (loc.width - 1) / 2) * px + px / 2;
    const cy = py(loc.y - (loc.height - 1) / 2) + px / 2;
    const r = Math.max(3, px * 0.8);
    const icon = readyIcon(style.icon);
    if (icon) {
      const s = r * 2.4;
      ctx.drawImage(icon, cx - s / 2, cy - s / 2, s, s);
    } else {
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.fillStyle = style.color;
      ctx.fill();
      ctx.lineWidth = Math.max(1, r * 0.18);
      ctx.strokeStyle = 'rgba(0,0,0,0.45)';
      ctx.stroke();
      if (showGlyphs) {
        ctx.fillStyle = '#1a1206';
        ctx.font = `${Math.floor(r * 1.5)}px Georgia, serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(style.glyph, cx, cy + r * 0.08);
        ctx.textAlign = 'start';
        ctx.textBaseline = 'alphabetic';
      }
    }
  }

  for (const e of dungeonService.getEntities()) {
    if (!seen(e.positionX, e.positionY)) {
      continue;
    }
    const cx = e.positionX * px + px / 2;
    const cy = py(e.positionY) + px / 2;
    if (e.type === DungeonEntityType.PLAYER) {
      const r = px * 0.85;
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.fillStyle = MAP_PALETTE.player;
      ctx.fill();
      ctx.lineWidth = Math.max(1, r * 0.3);
      ctx.strokeStyle = '#0c3d1d';
      ctx.stroke();
      continue;
    }
    const iconKind = (e as MonsterEntity).mapIcon;
    found.icons.add(iconKind);
    drawMonsterMarker(ctx, cx, cy, px, iconKind);
  }

  return found;
}

function drawMonsterMarker(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  px: number,
  iconKind: MonsterMapIcon,
): void {
  const style = MONSTER_ICON_STYLE[iconKind];
  const icon = readyIcon(style.icon);
  if (icon) {
    const s = px * 1.4;
    ctx.drawImage(icon, cx - s / 2, cy - s / 2, s, s);
    return;
  }
  ctx.fillStyle = style.color;
  ctx.strokeStyle = style.color;
  const r = px * 0.6;
  if (iconKind === 'elite') {
    ctx.lineWidth = Math.max(2, r * 0.5);
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.stroke();
  } else if (iconKind === 'miniboss') {
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(Math.PI / 4);
    const d = r * 1.7;
    ctx.fillRect(-d / 2, -d / 2, d, d);
    ctx.restore();
  } else if (iconKind === 'boss') {
    drawStar(ctx, cx, cy, r * 1.4, r * 0.6, 5);
    ctx.fill();
  } else {
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fill();
  }
}

function drawStar(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  outer: number,
  inner: number,
  points: number,
): void {
  ctx.beginPath();
  for (let i = 0; i < points * 2; i++) {
    const rad = i % 2 === 0 ? outer : inner;
    const a = (Math.PI / points) * i - Math.PI / 2;
    const x = cx + Math.cos(a) * rad;
    const y = cy + Math.sin(a) * rad;
    if (i === 0) {
      ctx.moveTo(x, y);
    } else {
      ctx.lineTo(x, y);
    }
  }
  ctx.closePath();
}
